import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import {
  type PrevailingInstrument,
} from '../components/proceedings/ProceedingDetailHeader';
import { type NPRMTimeline as FilingsNPRMTimeline } from '../components/proceedings/FilingsSection';
import {
  extractDeadlinesFromText,
  classifyDeadlinesFromExtracted,
  getFederalRegisterMainText,
  parseTimelineDeadlineDate,
  isTimelineRowStale,
} from '../utils/nprmHelpers';
import { findNPRMInFederalRegister } from '../utils/federalRegisterHelpers';
import { notifyCacheHit } from '../lib/cacheAlertService';

export interface ProceedingDetails {
  docket_number: string;
  proceeding_name: string;
  status: string;
  bureau: string | null;
  last_processed_at: string | null;
  last_document_count: number | null;
  processing_status: string;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface PublicNoticeDocumentRow {
  id_submission: string;
  date_received: string | null;
  filer_name: string | null;
  submissiontype_short: string | null;
  documents: any[];
}

export interface PublicNoticeDeadlines {
  commentDeadline: string | null;
  replyCommentDeadline: string | null;
  sourceSubmissionId: string | null;
}

/** Shape of nprm_timelines.{comment,reply}_deadline_extension JSONB. */
export interface DeadlineExtension {
  /** YYYY-MM-DD of the deadline before the extension. */
  from_date: string;
  /** ECFS submission_id of the order that extended the deadline. */
  by_submission_id: string;
  /** Filename of the order PDF (used for the display label, e.g. DA-26-510A1.pdf). */
  by_filename: string;
  /** YYYY-MM-DD of when the order was filed on the docket. */
  by_order_date: string;
  /** Optional: the bureau or party that filed the order. */
  by_filer?: string | null;
}

export interface DocketMetadata {
  /** True while the initial proceeding row fetch is in flight. */
  loading: boolean;
  /** Error message from the proceeding row fetch, if any. */
  error: string | null;
  proceeding: ProceedingDetails | null;

  // ── Raw data from get-docket-data edge function ────────────────────────
  fileCount: number | null;
  nonExpressFilingCount: number | null;
  fetchingFilingCount: boolean;
  nprmCount: number | null;
  nprmDates: string[];
  fetchingNprmCount: boolean;
  publicNoticeCount: number | null;
  publicNoticeDates: string[];
  publicNoticeDocuments: PublicNoticeDocumentRow[];
  publicNoticeDeadlines: PublicNoticeDeadlines | null;
  docketDataResolved: boolean;
  pnLooksLikeNprmFollowUp: boolean | null;

  // ── Resolved/derived ───────────────────────────────────────────────────
  commentDeadline: Date | null;
  replyCommentDeadline: Date | null;
  /**
   * Extension event for the comment deadline, when the FCC bureau filed
   * an order pushing the original date out. Surfaced in the proceedings
   * header so attorneys can see the date moved and click through to the
   * order PDF. NULL when no extension has been recorded for this docket.
   */
  commentDeadlineExtension: DeadlineExtension | null;
  replyDeadlineExtension: DeadlineExtension | null;
  fetchingDeadlines: boolean;
  prevailingInstrument: PrevailingInstrument;
  uploadCycleStartDate: string | null;
  topPublicNoticeDocument: PublicNoticeDocumentRow | null;
  topPublicNoticePdfUrl: string | null;
  syntheticPnTimelineForFilings: FilingsNPRMTimeline | null;

  // ── Mutations ──────────────────────────────────────────────────────────
  /** Re-fetch the proceeding row from the DB (e.g. after a status update). */
  refreshProceeding: () => Promise<void>;
  /** Apply a partial update to the in-memory proceeding row without a round-trip. */
  patchProceeding: (patch: Partial<ProceedingDetails>) => void;
}

function computePrevailingInstrument(
  nprmCount: number | null,
  publicNoticeCount: number | null,
  nprmDates: string[],
  publicNoticeDates: string[],
  pnLooksLikeNprmFollowUp: boolean | null
): PrevailingInstrument {
  const hasNprm = (nprmCount ?? 0) > 0;
  const hasPn = (publicNoticeCount ?? 0) > 0;
  if (hasNprm && !hasPn) return 'nprm';
  if (hasPn && !hasNprm) return 'public_notice';
  if (!hasNprm && !hasPn) return 'neither';
  if (hasNprm && hasPn && pnLooksLikeNprmFollowUp) return 'nprm';
  if (hasNprm && hasPn && pnLooksLikeNprmFollowUp !== false) return 'nprm';
  const nT = nprmDates[0] ? Date.parse(nprmDates[0]) : NaN;
  const pT = publicNoticeDates[0] ? Date.parse(publicNoticeDates[0]) : NaN;
  const nValid = !Number.isNaN(nT);
  const pValid = !Number.isNaN(pT);
  if (nValid && pValid) return pT > nT ? 'public_notice' : 'nprm';
  if (pValid && !nValid) return 'public_notice';
  if (nValid && !pValid) return 'nprm';
  return 'nprm';
}

/** Same PDF pick as `get-docket-data` extractPublicNoticeDeadlinesFromTopPn. */
function getPublicNoticeDocumentUrl(documents: any[] | undefined): string | null {
  if (!documents?.length) return null;
  const getDocUrl = (doc: any): string | null =>
    doc?.src || doc?.url || doc?.file_url || doc?.document_url || doc?.link || doc?.href || null;
  const pdfDoc =
    documents.find((d: any) => {
      const u = getDocUrl(d);
      return (
        u &&
        (String(u).toLowerCase().endsWith('.pdf') || String(d?.mime_type || '').toLowerCase().includes('pdf'))
      );
    }) || documents[0];
  const pdfUrl = getDocUrl(pdfDoc);
  return pdfUrl && pdfUrl !== '#' ? pdfUrl : null;
}

/**
 * Single source of truth for the docket-level metadata that the comment
 * summaries workflow needs: deadlines, NPRM/PN counts + dates + top PN doc,
 * prevailing instrument, and a synthetic timeline for PN-anchored dockets.
 *
 * Used by both `ProceedingDetailsPage` (full chrome) and `CommentSummariesPage`
 * (standalone) so the two views stay in lock-step. Auto-enroll / upload is
 * intentionally NOT in this hook — it's a side effect that the proceedings
 * page owns end-to-end (its UI shows upload progress) and would be confusing
 * to fire from the standalone summary route.
 */
export function useDocketMetadata(docketNumber: string | undefined): DocketMetadata {
  const [proceeding, setProceeding] = useState<ProceedingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [fileCount, setFileCount] = useState<number | null>(null);
  const [nonExpressFilingCount, setNonExpressFilingCount] = useState<number | null>(null);
  const [fetchingFilingCount, setFetchingFilingCount] = useState(false);
  const [nprmCount, setNprmCount] = useState<number | null>(null);
  const [nprmDates, setNprmDates] = useState<string[]>([]);
  const [fetchingNprmCount, setFetchingNprmCount] = useState(false);
  const [publicNoticeCount, setPublicNoticeCount] = useState<number | null>(null);
  const [publicNoticeDates, setPublicNoticeDates] = useState<string[]>([]);
  const [publicNoticeDocuments, setPublicNoticeDocuments] = useState<PublicNoticeDocumentRow[]>([]);
  const [publicNoticeDeadlines, setPublicNoticeDeadlines] = useState<PublicNoticeDeadlines | null>(null);
  const [docketDataResolved, setDocketDataResolved] = useState(false);
  const [pnLooksLikeNprmFollowUp, setPnLooksLikeNprmFollowUp] = useState<boolean | null>(null);

  const [commentDeadline, setCommentDeadline] = useState<Date | null>(null);
  const [commentDeadlineExtension, setCommentDeadlineExtension] =
    useState<DeadlineExtension | null>(null);
  const [replyDeadlineExtension, setReplyDeadlineExtension] =
    useState<DeadlineExtension | null>(null);
  const [replyCommentDeadline, setReplyCommentDeadline] = useState<Date | null>(null);
  const [fetchingDeadlines, setFetchingDeadlines] = useState(false);

  /** Supersedes in-flight resolveProceedingDeadlines so an older run cannot
   *  overwrite newer deadline state. */
  const deadlineResolveGenerationRef = useRef(0);

  const prevailingInstrument = useMemo(
    () =>
      computePrevailingInstrument(
        nprmCount,
        publicNoticeCount,
        nprmDates,
        publicNoticeDates,
        pnLooksLikeNprmFollowUp
      ),
    [nprmCount, publicNoticeCount, nprmDates, publicNoticeDates, pnLooksLikeNprmFollowUp]
  );

  const uploadCycleStartDate = useMemo(() => {
    if ((nprmCount ?? 0) > 0 && nprmDates[0]) return nprmDates[0];
    if ((publicNoticeCount ?? 0) > 0 && publicNoticeDates[0]) return publicNoticeDates[0];
    return null;
  }, [nprmCount, nprmDates, publicNoticeCount, publicNoticeDates]);

  const topPublicNoticeDocument = useMemo(
    () => publicNoticeDocuments[0] ?? null,
    [publicNoticeDocuments]
  );

  const topPublicNoticePdfUrl = useMemo(
    () => getPublicNoticeDocumentUrl(publicNoticeDocuments[0]?.documents),
    [publicNoticeDocuments]
  );

  const syntheticPnTimelineForFilings = useMemo((): FilingsNPRMTimeline | null => {
    if (prevailingInstrument !== 'public_notice') return null;
    if (!commentDeadline || !replyCommentDeadline || !docketNumber) return null;
    const pnStart =
      publicNoticeDocuments[0]?.date_received || publicNoticeDates[0] || null;
    if (!pnStart) return null;
    return {
      id: 'synthetic-pn',
      docket_number: docketNumber,
      comments_period_start: pnStart,
      comments_period_end: commentDeadline.toISOString(),
      reply_comments_period_start: null,
      reply_comments_period_end: replyCommentDeadline.toISOString(),
    };
  }, [
    prevailingInstrument,
    commentDeadline,
    replyCommentDeadline,
    docketNumber,
    publicNoticeDocuments,
    publicNoticeDates,
  ]);

  const fetchProceeding = useCallback(async () => {
    if (!docketNumber) return;
    try {
      setLoading(true);
      setError(null);
      const { data, error: dbError } = await supabase
        .from('open_proceedings')
        .select('*')
        .eq('docket_number', docketNumber)
        .single();
      if (dbError) {
        if (dbError.code === 'PGRST116') {
          throw new Error(`Proceeding "${docketNumber}" not found`);
        }
        throw dbError;
      }
      setProceeding(data);
    } catch (err) {
      console.error('[useDocketMetadata] fetchProceeding failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch proceeding details');
    } finally {
      setLoading(false);
    }
  }, [docketNumber]);

  const refreshProceeding = useCallback(async () => {
    if (!docketNumber) return;
    const { data, error: dbError } = await supabase
      .from('open_proceedings')
      .select('*')
      .eq('docket_number', docketNumber)
      .single();
    if (!dbError && data) {
      setProceeding(data);
    }
  }, [docketNumber]);

  const patchProceeding = useCallback((patch: Partial<ProceedingDetails>) => {
    setProceeding((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const fetchDocketData = useCallback(async () => {
    if (!docketNumber) return;
    try {
      setDocketDataResolved(false);
      setFetchingFilingCount(true);
      setFetchingNprmCount(true);

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-docket-data`;
      // Bound the request. get-docket-data can do live ECFS aggregation that is
      // slow (or hangs) on mega / mid-ingest dockets; without a timeout the
      // await never settles, the `finally` never runs, and the count spinners
      // spin forever. On abort we fall through to the catch → graceful "—".
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 25000);
      let response: Response;
      try {
        response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ docketNumber }),
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeoutId);
      }
      if (!response.ok) throw new Error(`Failed to fetch docket data: ${response.status}`);

      const data = await response.json();
      notifyCacheHit(data, `Docket data for ${docketNumber}`);

      if (data.filingCount !== null && data.filingCount !== undefined) {
        setFileCount(data.filingCount);
      } else if (proceeding?.last_document_count != null) {
        setFileCount(proceeding.last_document_count);
      } else {
        setFileCount(null);
      }

      setNonExpressFilingCount(
        data.nonExpressFilingCount !== null && data.nonExpressFilingCount !== undefined
          ? data.nonExpressFilingCount
          : null
      );

      if (data.nprmCount !== null && data.nprmCount !== undefined) {
        setNprmCount(data.nprmCount);
        setNprmDates(data.nprmDates || []);
      } else {
        setNprmCount(null);
        setNprmDates([]);
      }

      if (data.publicNoticeCount !== null && data.publicNoticeCount !== undefined) {
        setPublicNoticeCount(data.publicNoticeCount);
        setPublicNoticeDates(data.publicNoticeDates || []);
        setPublicNoticeDocuments(data.publicNoticeDocuments || []);
      } else {
        setPublicNoticeCount(null);
        setPublicNoticeDates([]);
        setPublicNoticeDocuments([]);
      }

      setPublicNoticeDeadlines(data.publicNoticeDeadlines ?? null);
    } catch (err) {
      console.error('[useDocketMetadata] fetchDocketData failed:', err);
      if (proceeding?.last_document_count != null) {
        setFileCount(proceeding.last_document_count);
      } else {
        setFileCount(null);
      }
      setNonExpressFilingCount(null);
      setNprmCount(null);
      setNprmDates([]);
      setPublicNoticeCount(null);
      setPublicNoticeDates([]);
      setPublicNoticeDocuments([]);
      setPublicNoticeDeadlines(null);
    } finally {
      setFetchingFilingCount(false);
      setFetchingNprmCount(false);
      setDocketDataResolved(true);
    }
  }, [docketNumber, proceeding]);

  const resolveDeadlines = useCallback(
    async (generation: number) => {
      if (!docketNumber || !proceeding) return;
      setFetchingDeadlines(true);

      try {
        let comment: Date | null = null;
        let reply: Date | null = null;

        const applyPnServerDeadlines = () => {
          if (
            publicNoticeDeadlines &&
            (publicNoticeDeadlines.commentDeadline || publicNoticeDeadlines.replyCommentDeadline)
          ) {
            if (!comment && publicNoticeDeadlines.commentDeadline) {
              comment = parseTimelineDeadlineDate(publicNoticeDeadlines.commentDeadline);
            }
            if (!reply && publicNoticeDeadlines.replyCommentDeadline) {
              reply = parseTimelineDeadlineDate(publicNoticeDeadlines.replyCommentDeadline);
            }
          }
        };

        const applyPnDbTextDeadlines = async () => {
          const stillNeedComment = !comment;
          const stillNeedReply = !reply;
          if ((!stillNeedComment && !stillNeedReply) || publicNoticeDocuments.length === 0) return;
          try {
            const submissionIds = publicNoticeDocuments.map((d) => d.id_submission);
            const { data: pnRows, error: pnErr } = await supabase
              .from('filings')
              .select('submission_id, text_content, date_received')
              .eq('docket_number', docketNumber)
              .in('submission_id', submissionIds)
              .not('text_content', 'is', null)
              .order('date_received', { ascending: false });
            if (pnErr) console.warn('PN deadline fallback query failed:', pnErr.message);

            for (const row of pnRows || []) {
              if (!row.text_content) continue;
              const extracted = extractDeadlinesFromText(row.text_content);
              if (extracted.length === 0) continue;
              const classified = classifyDeadlinesFromExtracted(extracted, row.text_content);
              if (!comment && classified.foundCommentDeadline) {
                comment = parseTimelineDeadlineDate(classified.foundCommentDeadline.toISOString());
              }
              if (!reply && classified.foundReplyCommentDeadline) {
                reply = parseTimelineDeadlineDate(classified.foundReplyCommentDeadline.toISOString());
              }
              if (comment && reply) break;
            }
          } catch (pnFallbackErr) {
            console.error('Public Notice deadline fallback failed:', pnFallbackErr);
          }
        };

        if (prevailingInstrument === 'public_notice') {
          applyPnServerDeadlines();
          await applyPnDbTextDeadlines();
        } else {
          const { data: timelines, error: tlError } = await supabase
            .from('nprm_timelines')
            .select(
              'fcc_release_date, federal_register_publication_date, comment_deadline, reply_comment_deadline, comment_deadline_extension, reply_deadline_extension'
            )
            .eq('docket_number', docketNumber)
            .order('fcc_release_date', { ascending: false });

          if (!tlError && timelines && timelines.length > 0) {
            const row = timelines[0];
            if (!isTimelineRowStale(row)) {
              if (row.comment_deadline) comment = parseTimelineDeadlineDate(row.comment_deadline);
              if (row.reply_comment_deadline) {
                reply = parseTimelineDeadlineDate(row.reply_comment_deadline);
              }
            }
            // Extension columns are independent of the staleness gate —
            // they're metadata about why the deadline moved, not a date
            // we'd want to suppress just because the timeline's stale.
            const cExt = (row as { comment_deadline_extension?: DeadlineExtension | null })
              .comment_deadline_extension ?? null;
            const rExt = (row as { reply_deadline_extension?: DeadlineExtension | null })
              .reply_deadline_extension ?? null;
            setCommentDeadlineExtension(cExt);
            setReplyDeadlineExtension(rExt);
          }

          const needComment = !comment;
          const needReply = !reply;
          const hasEcfsNprm = (nprmCount ?? 0) > 0;
          const latestEcfsNprmDate =
            nprmDates[0] && !Number.isNaN(Date.parse(nprmDates[0])) ? new Date(nprmDates[0]) : null;

          if (hasEcfsNprm && (needComment || needReply)) {
            try {
              const nprmTitles = proceeding.proceeding_name
                ? [proceeding.proceeding_name]
                : undefined;
              const matchResult = await findNPRMInFederalRegister(
                docketNumber,
                nprmTitles,
                latestEcfsNprmDate ?? undefined
              );
              const matchedDoc = matchResult.document;

              if (matchedDoc?.comments_close_on && !comment) {
                comment = parseTimelineDeadlineDate(matchedDoc.comments_close_on);
              }
              if (matchedDoc?.dates && (!comment || !reply)) {
                const datesExtracted = extractDeadlinesFromText(matchedDoc.dates);
                const datesClassified = classifyDeadlinesFromExtracted(
                  datesExtracted,
                  matchedDoc.dates
                );
                if (!comment && datesClassified.foundCommentDeadline) {
                  comment = parseTimelineDeadlineDate(
                    datesClassified.foundCommentDeadline.toISOString()
                  );
                }
                if (!reply && datesClassified.foundReplyCommentDeadline) {
                  reply = parseTimelineDeadlineDate(
                    datesClassified.foundReplyCommentDeadline.toISOString()
                  );
                }
              }

              if (matchedDoc?.html_url && (!comment || !reply)) {
                const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-federal-register-html`;
                const response = await fetch(apiUrl, {
                  method: 'POST',
                  headers: {
                    Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({ htmlUrl: matchedDoc.html_url }),
                });
                if (response.ok) {
                  const result = await response.json();
                  const textContent = getFederalRegisterMainText(result.html);
                  const extracted = extractDeadlinesFromText(textContent);
                  const classified = classifyDeadlinesFromExtracted(extracted, textContent);
                  if (!comment && classified.foundCommentDeadline) {
                    comment = parseTimelineDeadlineDate(
                      classified.foundCommentDeadline.toISOString()
                    );
                  }
                  if (!reply && classified.foundReplyCommentDeadline) {
                    reply = parseTimelineDeadlineDate(
                      classified.foundReplyCommentDeadline.toISOString()
                    );
                  }
                } else {
                  const errorText = await response.text().catch(() => 'Unknown error');
                  console.warn(
                    'Failed to fetch Federal Register document HTML:',
                    response.status,
                    errorText
                  );
                }
              }
            } catch (frErr) {
              console.error('Federal Register fallback for deadlines failed:', frErr);
            }
          }

          const stillNeedComment = !comment;
          const stillNeedReply = !reply;
          if ((stillNeedComment || stillNeedReply) && publicNoticeDocuments.length > 0) {
            await applyPnDbTextDeadlines();
          }

          if (
            (!comment || !reply) &&
            publicNoticeDeadlines &&
            (publicNoticeDeadlines.commentDeadline || publicNoticeDeadlines.replyCommentDeadline)
          ) {
            applyPnServerDeadlines();
          }
        }

        if (generation !== deadlineResolveGenerationRef.current) return;
        setCommentDeadline(comment);
        setReplyCommentDeadline(reply);
      } catch (err) {
        console.error('[useDocketMetadata] resolveDeadlines failed:', err);
        if (generation !== deadlineResolveGenerationRef.current) return;
        setCommentDeadline(null);
        setReplyCommentDeadline(null);
      } finally {
        if (generation === deadlineResolveGenerationRef.current) {
          setFetchingDeadlines(false);
        }
      }
    },
    [
      docketNumber,
      proceeding,
      publicNoticeDocuments,
      publicNoticeDeadlines,
      prevailingInstrument,
      nprmCount,
      nprmDates,
    ]
  );

  // ── Effects ──────────────────────────────────────────────────────────

  // 1. Reset PN-derived state on docket change so a stale value doesn't
  //    bleed into the next docket while its docket-data is in flight.
  useEffect(() => {
    setPublicNoticeDeadlines(null);
    setPnLooksLikeNprmFollowUp(null);
    setDocketDataResolved(false);
  }, [docketNumber]);

  // 2. Fetch the proceeding row on docket change.
  useEffect(() => {
    if (docketNumber) fetchProceeding();
  }, [docketNumber, fetchProceeding]);

  // 3. After proceeding loads, fetch the docket data bundle.
  useEffect(() => {
    if (docketNumber && proceeding) fetchDocketData();
  }, [docketNumber, proceeding, fetchDocketData]);

  // 4. Resolve deadlines after proceeding loads (and re-runs when PN
  //    deadlines arrive from get-docket-data).
  useEffect(() => {
    if (!docketNumber || !proceeding) return;
    const gen = ++deadlineResolveGenerationRef.current;
    void resolveDeadlines(gen);
  }, [docketNumber, proceeding, resolveDeadlines, publicNoticeDeadlines]);

  // 5. Infer whether the top PN is a procedural follow-up to an NPRM
  //    (drives prevailing-instrument when both exist).
  useEffect(() => {
    let cancelled = false;
    const inferPnRelationship = async () => {
      const hasNprm = (nprmCount ?? 0) > 0;
      const sourceSubmissionId =
        publicNoticeDeadlines?.sourceSubmissionId ?? publicNoticeDocuments[0]?.id_submission;
      if (!hasNprm || !sourceSubmissionId) {
        setPnLooksLikeNprmFollowUp(null);
        return;
      }
      try {
        const { data, error: rpcErr } = await supabase.rpc('pn_looks_like_nprm_followup', {
          p_docket_number: docketNumber,
          p_submission_id: sourceSubmissionId,
        });
        if (cancelled) return;
        if (rpcErr) {
          setPnLooksLikeNprmFollowUp(null);
          return;
        }
        setPnLooksLikeNprmFollowUp(typeof data === 'boolean' ? data : null);
      } catch (relErr) {
        console.warn('[useDocketMetadata] inferPnRelationship failed:', relErr);
        if (!cancelled) setPnLooksLikeNprmFollowUp(null);
      }
    };
    void inferPnRelationship();
    return () => {
      cancelled = true;
    };
  }, [docketNumber, nprmCount, publicNoticeDeadlines, publicNoticeDocuments]);

  return {
    loading,
    error,
    proceeding,
    fileCount,
    nonExpressFilingCount,
    fetchingFilingCount,
    nprmCount,
    nprmDates,
    fetchingNprmCount,
    publicNoticeCount,
    publicNoticeDates,
    publicNoticeDocuments,
    publicNoticeDeadlines,
    docketDataResolved,
    pnLooksLikeNprmFollowUp,
    commentDeadline,
    replyCommentDeadline,
    commentDeadlineExtension,
    replyDeadlineExtension,
    fetchingDeadlines,
    prevailingInstrument,
    uploadCycleStartDate,
    topPublicNoticeDocument,
    topPublicNoticePdfUrl,
    syntheticPnTimelineForFilings,
    refreshProceeding,
    patchProceeding,
  };
}
